import type {
  AttachKind,
  DrawingKind,
  OrgKind,
  OrgStatus,
  PunchSource,
  PunchStatus,
  PunchType,
  PurchaseStatus,
  ScheduleSourceRef,
  ScheduleStatus,
  ScheduleTaskInput,
  ScheduleTaskType,
  SiteStatus,
  UserStatus,
} from '@construction/shared';
import { api } from './client';

// ---------------------------------------------------------------- 类型

/**
 * 挂在账号上的角色（CR-038）。名字是客户输入的数据，不翻译；
 * `isSystem` 那条是超管，隐式拥有全部权限，permissions 列不看。
 */
export interface RoleSummary {
  id: number;
  name: string;
  isSystem: boolean;
  permissions: string[];
}

/** 单位（CR-073：多租户时是「公司」，单公司部署时是「分公司」）的名字引用 */
export interface OrgRef {
  id: number;
  name: string;
}

/** 单位页的一行。单位账号只拿得到自己那一家 */
export interface Org extends OrgRef {
  status: OrgStatus;
  createdAt: string;
  _count: { users: number; sites: number };
}

export interface Role extends RoleSummary {
  description: string | null;
  /** null = 平台共享（预置角色、超管建的模板），各家能用不能改；非空 = 那家自建的（CR-073） */
  orgId: number | null;
  org: OrgRef | null;
  createdAt: string;
  updatedAt: string;
  /** 挂在这个角色上的人数（单位账号只数自己家的）。删之前要看 */
  _count: { users: number };
}

export interface AuthUser {
  id: number;
  employeeNo: string;
  name: string;
  role: RoleSummary;
  /** null = 平台账号，看得到所有单位（CR-073） */
  orgId: number | null;
  /** 部署模式：`company` 叫「公司 / 平台」，`branch` 叫「分公司 / 总部」（CR-073） */
  orgKind: OrgKind;
  locale: string;
  mustChangePassword: boolean;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: string;
  user: AuthUser;
}

export interface Site {
  id: number;
  name: string;
  nameEn: string | null;
  address: string | null;
  lat: number;
  lng: number;
  geofenceRadiusM: number;
  /** 最长班次（分钟）：上班超过这么久还没打下班卡才算漏打（CR-052） */
  maxShiftMinutes: number;
  timezone: string;
  currency: string;
  defaultRateCents: number | null;
  status: SiteStatus;
  /** 归哪家（CR-073）。工地必属于一家 */
  orgId: number;
  org: OrgRef;
  createdAt: string;
  /** 在场成员数（只数指派的）。只有工地列表带，单个工地的接口没有（CR-082） */
  memberCount?: number;
}

export interface SiteMember {
  id: string;
  joinedAt: string;
  user: {
    id: number;
    employeeNo: string;
    name: string;
    phone: string | null;
    role: RoleSummary;
    status: UserStatus;
    hourlyRateCents: number | null;
  };
}

export interface User {
  id: number;
  employeeNo: string;
  name: string;
  phone: string | null;
  roleId: number;
  role: RoleSummary & { orgId: number | null };
  /** null = 平台账号（CR-073） */
  orgId: number | null;
  org: OrgRef | null;
  locale: string;
  hourlyRateCents: number | null;
  status: UserStatus;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface Punch {
  id: string;
  userId: number;
  siteId: number;
  type: PunchType;
  occurredAt: string;
  receivedAt: string;
  /** 拿不到定位时为 null（CR-015）。渲染前必须判空，别直接 toFixed */
  lat: number | null;
  lng: number | null;
  accuracyM: number | null;
  distanceM: number | null;
  /** 「继续打卡」时工人选的理由和自己写的说明 */
  overrideReason?: string | null;
  overrideReasonNote?: string | null;
  isMocked: boolean;
  source: PunchSource;
  status: PunchStatus;
  reason: string | null;
  reviewNote: string | null;
  user?: { id: number; employeeNo: string; name: string };
  /** 这张卡引起的异常已标记处理（CR-052）。列表接口才带 */
  anomalyResolution?: {
    note: string;
    resolvedAt: string;
    resolvedById: number;
    resolvedByName: string | null;
  } | null;
}

export interface CurrentShift {
  open: boolean;
  /** 最后一条上班卡超过了上限（忘打下班卡）。不算在班，但界面要提示 */
  stale: boolean;
  elapsedMinutes: number;
  maxShiftMinutes: number;
  /** 服务端此刻的时间。手机时钟常常不准，计时器拿它算一次偏差 */
  serverNow: string;
  punch: { id: string; siteId: number; type: PunchType; occurredAt: string } | null;
  /** 完整工地对象：人被移出工地或工地归档后，/sites 里就没有它了，
      但这个班还得能闭上，所以随班次一起带回来 */
  site: Site | null;
}

/** 按人汇总的考勤（CR-025）。服务端算，因为列表接口有 500 条截断 */
export interface AttendanceSummary {
  timezone: string;
  /** 工地的最长班次（CR-052）：展开层判断在岗 / 缺下班卡用 */
  maxShiftMinutes: number;
  people: AttendancePerson[];
  totals: {
    people: number;
    workedMinutes: number;
    pendingCount: number;
    anomalyCount: number;
  };
}

export interface AttendancePerson {
  user: { id: number; employeeNo: string; name: string };
  /** 有上班卡的天数。忘打下班卡那天也算 —— 人确实来了 */
  workedDays: number;
  /** 配成对的班次总分钟数。没配上的不计入，所以它可能比「天数 × 8」少 */
  workedMinutes: number;
  punchCount: number;
  pendingCount: number;
  anomalyCount: number;
  /** 最后一张上班卡还在最长班次之内：在岗，不算异常（CR-052） */
  onShift: boolean;
  /** 在岗的话，从几点开始（页头补录选人用） */
  onShiftSince: string | null;
  days: Array<{ date: string; minutes: number }>;
  /** 有没处理的异常的日期（工地当地 YYYY-MM-DD，CR-082 周表标红） */
  anomalyDates: string[];
  /** 有待复核打卡的日期（周表标橙） */
  pendingDates: string[];
}

/** 「他平时几点」：补卡的建议时间 */
export interface UsualTime {
  /** 工地当地 `HH:MM`，取整到 5 分钟 */
  time: string;
  /** 他自己的，还是全工地的（他记录太少时） */
  basis: 'person' | 'site';
}

/** 补卡表单的上下文 */
export interface BackfillContext {
  date: string;
  maxShiftMinutes: number;
  /** 所选日期（工地时区）前后各多一个最长班次内没被拒的打卡，时间升序（CR-066：夜班的上班卡在前一晚） */
  punches: Array<Pick<Punch, 'id' | 'type' | 'occurredAt' | 'status'>>;
  /** 他此刻开着的那张上班卡：补的是这一班的下班卡时才问「要不要结束登录」 */
  openShiftPunchId: string | null;
  usual: { clockIn: UsualTime | null; clockOut: UsualTime | null };
}

/** 「异常」页签的一条（CR-052）：引起异常的那张打卡 + 原因 */
export interface AttendanceAnomalyItem {
  punch: Punch;
  reason: 'MISSING_CLOCK_OUT' | 'MISSING_CLOCK_IN' | 'SESSION_TOO_LONG';
  /** SESSION_TOO_LONG 时算出来的时长（分钟） */
  minutes?: number;
}

/** 考勤页「待复核」「异常」两个页签，不限日期（CR-052） */
export interface AttendanceExceptions {
  maxShiftMinutes: number;
  pending: Punch[];
  anomalies: AttendanceAnomalyItem[];
}

export interface TodayPunches {
  punches: Punch[];
  clockedIn: boolean;
  nextAction: PunchType;
  timezone: string;
}

export interface HiddenWorkPhoto {
  id: string;
  sha256: string;
  takenAt: string | null;
  /** 服务端收到的时刻。和 takenAt 差得多说明是离线补传（CR-033） */
  uploadedAt?: string;
  /** 隐蔽工程也收视频（CR-023），前端靠 kind / mimeType 决定渲染 img 还是 video */
  kind: AttachKind;
  mimeType: string;
  url: string;
  thumbUrl: string | null;
}

export interface HiddenWork {
  id: string;
  siteId: number;
  category: string;
  locationDesc: string;
  note: string | null;
  lat: number | null;
  lng: number | null;
  gpsAccuracyM: number | null;
  capturedAt: string;
  serverAt: string;
  photos: HiddenWorkPhoto[];
  user?: { id: number; employeeNo: string; name: string };
}

// ---------------------------------------------------------------- 上传

/** 已经直传完成、等待登记的文件。presign → PUT → 把这个交给业务接口 */
export interface UploadedFileRef {
  clientId: string;
  fileKey: string;
  mimeType: string;
  sha256: string;
}

// ---------------------------------------------------------------- 日报

/**
 * 日报上挂的一件东西：图片或视频（CR-023）。
 *
 * 字段名还叫 `photos` 是因为服务端那一侧沿用了旧名字；类型名用 `LogMedia`
 * 而不是 `DailyLogPhoto`，免得读代码的人以为里面只有照片。
 * 视频没有缩略图（服务端不跑 ffmpeg），`thumbUrl` 是 null。
 */
export interface LogMedia {
  id: string;
  kind: AttachKind;
  mimeType: string;
  /** 设备给的拍摄时刻，可能没有（从相册选的、或者机型不给） */
  takenAt?: string | null;
  /** 服务端收到这个文件的时刻（CR-033） */
  uploadedAt?: string;
  url: string;
  thumbUrl: string | null;
}

export interface DailyLog {
  id: string;
  siteId: number;
  userId: number;
  workDate: string;
  content: string | null;
  createdAt: string;
  photos: LogMedia[];
  user?: { id: number; employeeNo: string; name: string };
}

export interface Drawing {
  id: string;
  siteId: number;
  /** DRAWING = 施工图纸；SCHEDULE = 进度表，每个工地只有当前一张（CR-023） */
  kind: DrawingKind;
  title: string;
  category: string | null;
  fileKey: string;
  mimeType: string;
  sizeBytes: number;
  pageCount: number | null;
  version: number;
  supersedesId: string | null;
  superseded?: boolean;
  createdAt: string;
  url: string;
  thumbUrl?: string | null;
  pins?: DrawingPin[];
  _count?: { pins: number };
}

export interface DrawingPin {
  id: string;
  drawingId: string;
  page: number;
  x: number;
  y: number;
  label: string | null;
  /** 详细说明。label 装不下的现场情况写这里 */
  note: string | null;
  hiddenWorkId: string | null;
  /** 关联对象的摘要，图纸详情接口一并返回，省得每个图钉再打一次请求 */
  hiddenWork?: { id: string; category: string; locationDesc: string } | null;
}

/** 本工地在职成员，按人筛选的下拉用 */
export interface MemberOption {
  id: number;
  employeeNo: string;
  name: string;
}

/** 审计日志一条（CR-040 补上的页面） */
export interface AuditLogEntry {
  id: string;
  actorId: number | null;
  action: string;
  entityType: string;
  entityId: string;
  /** 对象的名字（CR-041）：账号「姓名 · 工号」，工地成员 / 打卡 / 采购 / 日报「谁 · 哪个工地」；查不到为 null */
  entityLabel: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  /** 归哪家（CR-073）；null = 平台上的事 */
  orgId: number | null;
  createdAt: string;
  actor: { id: number; employeeNo: string; name: string } | null;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ---------------------------------------------------------------- 接口

export const authApi = {
  login: (employeeNo: string, password: string, deviceId?: string) =>
    api.post<LoginResponse>('/auth/login', { employeeNo, password, deviceId }),

  logout: (refreshToken: string) => api.post<void>('/auth/logout', { refreshToken }),

  me: () => api.get<AuthUser>('/auth/me'),

  /** 改密会作废旧会话，所以服务端直接返回一套新 token，前端存下即可继续用 */
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ accessToken: string; refreshToken: string; expiresIn: string }>(
      '/auth/change-password',
      { currentPassword, newPassword },
    ),
};

export interface GeocodeResult {
  address: string;
  label: string;
  lat: number;
  lng: number;
}

export const geocodeApi = {
  /**
   * 地址搜坐标。上游限了每秒一次，调用方要自己防抖。
   *
   * **不传界面语言**：地址一律返回英文，理由见后端 geocode.service.ts 的
   * ADDRESS_LANGUAGE —— 中文界面下 Nominatim 会给回半中半英的地址。
   */
  search: (q: string) => api.get<GeocodeResult[]>('/geocode/search', { q }),

  /** 坐标反查地址 */
  reverse: (lat: number, lng: number) =>
    api.get<GeocodeResult | null>('/geocode/reverse', { lat, lng }),
};

export const sitesApi = {
  list: (includeArchived = false) =>
    api.get<Site[]>('/sites', includeArchived ? { includeArchived: true } : undefined),

  get: (id: number) => api.get<Site>(`/sites/${id}`),

  create: (body: {
    name: string;
    /** 平台账号必须选（CR-073）；单位账号不传，建的永远是自己家的 */
    orgId?: number;
    nameEn?: string;
    address?: string;
    lat: number;
    lng: number;
    geofenceRadiusM?: number;
    timezone?: string;
    currency?: string;
    defaultRateCents?: number;
  }) => api.post<Site>('/sites', body),

  /** 传 null 表示清空该字段（后端 @IsOptional 对 null 放行） */
  update: (
    id: number,
    body: Partial<Omit<Site, 'id' | 'createdAt' | 'currency'>> & {
      defaultRateCents?: number | null;
    },
  ) => api.patch<Site>(`/sites/${id}`, body),

  members: (siteId: number) => api.get<SiteMember[]>(`/sites/${siteId}/members`),

  /** 按人筛选用：只有 id、工号、姓名。看别人记录的页面用它，不用 members（CR-040） */
  memberOptions: (siteId: number) => api.get<MemberOption[]>(`/sites/${siteId}/member-options`),

  /** 一次加几个人（CR-071）；已在场的跳过，返回真正加进去的 */
  addMembers: (siteId: number, userIds: number[]) =>
    api.post<{ added: number[] }>(`/sites/${siteId}/members`, { userIds }),

  removeMember: (siteId: number, userId: number) =>
    api.delete<void>(`/sites/${siteId}/members/${userId}`),
};

export const attendanceApi = {
  /**
   * 当前班次。工地锁定和计时都读它。
   * 不带 siteId —— 它要回答的就是「我现在在哪个工地」。
   */
  currentShift: () => api.get<CurrentShift>('/me/shift'),

  today: (siteId: number) => api.get<TodayPunches>(`/sites/${siteId}/me/punches/today`),

  list: (siteId: number, params: { from?: string; to?: string; userId?: number; status?: string }) =>
    api.get<Punch[]>(`/sites/${siteId}/punches`, params),

  /**
   * 按人汇总。**不要在前端拿 list() 的结果自己汇总** ——
   * 那个接口 `take: 500`，两周 20 个人就超了，算出来的总工时会静默少算。
   */
  summary: (siteId: number, params: { from?: string; to?: string }) =>
    api.get<AttendanceSummary>(`/sites/${siteId}/attendance/summary`, params),

  review: (punchId: string, status: PunchStatus, note?: string) =>
    api.post<Punch>(`/punches/${punchId}/review`, { status, note }),

  /** 补卡表单：这天前后的打卡、他平时几点上下班、要不要结束登录 */
  backfillContext: (siteId: number, userId: number, date: string) =>
    api.get<BackfillContext>(`/sites/${siteId}/attendance/backfill-context`, { userId, date }),

  /** 待复核 + 异常，不限日期、最早的在前（CR-052） */
  exceptions: (siteId: number) => api.get<AttendanceExceptions>(`/sites/${siteId}/attendance/exceptions`),

  /** 补不了的异常写说明标记已处理；不改打卡、不改工时（CR-052） */
  resolveAnomaly: (punchId: string, note: string) =>
    api.post<unknown>(`/punches/${punchId}/anomaly-resolution`, { note }),

  unresolveAnomaly: (punchId: string) => api.delete<unknown>(`/punches/${punchId}/anomaly-resolution`),

  manual: (body: {
    siteId: number;
    userId: number;
    type: PunchType;
    occurredAt: string;
    /** 补整段（CR-066）：下班时刻，和 `type: IN` 一起传，一次建上班、下班两张卡 */
    endAt?: string;
    note: string;
    /** 单补下班卡时是否同时作废该工人手机上的会话（CR-006），默认 true */
    endSession?: boolean;
  }) => api.post<Punch & { sessionEnded: boolean; punches: Punch[] }>('/punches/manual', body),
};

export const hiddenWorksApi = {
  list: (
    siteId: number,
    params: { category?: string; userId?: number; from?: string; to?: string } = {},
  ) => api.get<HiddenWork[]>(`/sites/${siteId}/hidden-works`, params),

  categories: (siteId: number) => api.get<string[]>(`/sites/${siteId}/hidden-works/categories`),

  get: (id: string) => api.get<HiddenWork>(`/hidden-works/${id}`),

  /** 我拍过的（CR-040）。拍的人永远能看自己拍的，不需要「查看全工地」 */
  /** CR-048：和「本工地」同一套区间 + 类别，服务端筛 */
  mine: (siteId: number, params: { category?: string; from?: string; to?: string } = {}) =>
    api.get<HiddenWork[]>('/me/hidden-works', { siteId, ...params }),
};

export const dailyLogsApi = {
  /** 我在这个工地写过的（CR-042：一律带工地；CR-048：和「本工地」同一套区间，服务端筛） */
  myLogs: (siteId: number, params: { from?: string; to?: string } = {}) =>
    api.get<DailyLog[]>('/me/daily-logs', { siteId, ...params }),

  /**
   * 日报列表。**返回的是分页结构，不是裸数组**（CR-022）——
   * 改之前服务端 take: 300 硬截断且不返回总数，超出的日报静默消失。
   */
  list: (
    siteId: number,
    params: {
      userId?: number;
      from?: string;
      to?: string;
      page?: number;
      pageSize?: number;
    } = {},
  ) => api.get<Paginated<DailyLog>>(`/sites/${siteId}/daily-logs`, params),
};

export const drawingsApi = {
  /** 原文件的另存为地址（5 分钟有效，CR-043）。按 kind 要 drawing.export / schedule.export */
  downloadUrl: (id: string) => api.get<{ url: string; filename: string }>(`/drawings/${id}/download`),
  list: (siteId: number, params: { includeSuperseded?: boolean; kind?: DrawingKind } = {}) =>
    api.get<Drawing[]>(`/sites/${siteId}/drawings`, {
      ...(params.includeSuperseded ? { includeSuperseded: true } : {}),
      ...(params.kind ? { kind: params.kind } : {}),
    }),

  /**
   * 当前这个工地的进度表，没有就是 null（CR-023）。
   *
   * 「传新的替换旧的」走的是图纸已有的版本链：新的 `supersedesId` 指向旧的，
   * 而 list 本来就会滤掉被替代的版本 —— 所以这个查询天然只会回来一张。
   */
  schedule: async (siteId: number): Promise<Drawing | null> => {
    const list = await api.get<Drawing[]>(`/sites/${siteId}/drawings`, { kind: 'SCHEDULE' });
    return list[0] ?? null;
  },

  get: (id: string) => api.get<Drawing>(`/drawings/${id}`),

  versions: (id: string) =>
    api.get<Array<{ id: string; version: number; title: string; createdAt: string }>>(
      `/drawings/${id}/versions`,
    ),

  create: (
    siteId: number,
    body: {
      title: string;
      category?: string;
      fileKey: string;
      mimeType: string;
      sizeBytes?: number;
      pageCount?: number;
      supersedesId?: string;
      kind?: DrawingKind;
    },
  ) => api.post<Drawing>(`/sites/${siteId}/drawings`, body),

  addPin: (
    drawingId: string,
    body: {
      page?: number;
      x: number;
      y: number;
      /** 必填：没有说明的图钉就是工人看到的那种无意义的点 */
      label: string;
      note?: string;
      hiddenWorkId?: string;
    },
  ) => api.post<DrawingPin>(`/drawings/${drawingId}/pins`, body),

  removePin: (pinId: string) => api.delete<void>(`/drawings/pins/${pinId}`),
};

/**
 * 进度表的结构化数据（CR-080）。和服务端 `schedule-view.ts` 的 `ScheduleView` 同形 ——
 * 甘特图按它画，给 AI / 脚本的也是这一份。工作日、日历天、停工日是服务端读时算的。
 */
export interface ScheduleData {
  schema: string;
  id: string;
  status: ScheduleStatus;
  site: { id: number; name: string; timezone: string };
  source: { drawingId: string; version: number; title: string; mimeType: string; uploadedAt: string; parser: string | null };
  calendar: { workdays: string[]; nonWorkDates: string[] };
  tasks: ScheduleTaskData[];
  updatedAt: string;
  updatedBy: { id: number; name: string } | null;
  publishedAt: string | null;
  publishedBy: { id: number; name: string } | null;
}

export interface ScheduleTaskData {
  id: string;
  code: string;
  name: string;
  type: ScheduleTaskType;
  plannedStart: string;
  plannedFinish: string;
  workdays: number | null;
  calendarDays: number | null;
  color: string | null;
  sourceRef: ScheduleSourceRef[];
}

export const scheduleApi = {
  /** 当前那一版进度表文件（`drawingId`）和它的数据。草稿只回给能换进度表的人 */
  get: (siteId: number) =>
    api.get<{ drawingId: string | null; schedule: ScheduleData | null }>(`/sites/${siteId}/schedule`),

  /** 存核对表（草稿）或发布。`drawingId` 不是当前那一版就回 SCHEDULE_OUTDATED */
  save: (
    siteId: number,
    body: {
      drawingId: string;
      parser?: string | null;
      workdays: number[];
      tasks: ScheduleTaskInput[];
      publish: boolean;
    },
  ) => api.put<ScheduleData>(`/sites/${siteId}/schedule`, body),
};

export const usersApi = {
  /** search 会同时匹配工号、姓名、电话（服务端 OR 查询） */
  list: (params: {
    search?: string;
    roleId?: number;
    status?: UserStatus;
    /** 平台账号按单位筛（CR-073）：单位 id，或 `platform` = 只看平台账号 */
    org?: string;
    page?: number;
    pageSize?: number;
  }) => api.get<Paginated<User>>('/users', params),

  get: (id: number) => api.get<User>(`/users/${id}`),

  create: (body: {
    name: string;
    phone?: string;
    roleId: number;
    /** 平台账号必须传：单位 id，或 null = 平台账号（只有超管）。单位账号不传（CR-073） */
    orgId?: number | null;
    hourlyRateCents?: number;
  }) => api.post<{ user: User; initialPassword: string }>('/users', body),

  update: (
    id: number,
    body: Partial<{
      name: string;
      phone: string;
      roleId: number;
      /** 挪到别家（CR-073，只有平台账号能挪） */
      orgId: number | null;
      hourlyRateCents: number;
      status: UserStatus;
    }>,
  ) => api.patch<User>(`/users/${id}`, body),

  resetPassword: (id: number) =>
    api.post<{ initialPassword: string }>(`/users/${id}/reset-password`),

  /** 直接写入指定密码（CR-077）。门槛和重置一样；`mustChange` 不传 = 不要求本人再改 */
  setPassword: (id: number, body: { password: string; mustChange?: boolean }) =>
    api.post<void>(`/users/${id}/set-password`, body),

  /** 这个人在哪些工地（在职成员关系，CR-071），用户页勾工地时回显 */
  sites: (id: number) => api.get<number[]>(`/users/${id}/sites`),

  /** 直接指定所在工地（CR-071）：给全量，服务端只动操作者看得见的在建工地 */
  setSites: (id: number, siteIds: number[]) =>
    api.put<{ siteIds: number[]; added: number[]; removed: number[] }>(`/users/${id}/sites`, {
      siteIds,
    }),

  updateMyLocale: (locale: string) => api.patch<User>('/me/preferences', { locale }),
};

/**
 * 角色管理（CR-038）。读给建账号的下拉用。
 * 写：共享角色只有超管；单位自建的要 role.manage + 子集规则（CR-073，`canEditRole`）
 */
export const rolesApi = {
  list: () => api.get<Role[]>('/roles'),

  /** `orgId`：平台账号必须传（单位 id，或 null = 共享，只有超管）；单位账号不传 */
  create: (body: { name: string; description?: string; permissions: string[]; orgId?: number | null }) =>
    api.post<Role>('/roles', body),

  update: (
    id: number,
    body: Partial<{ name: string; description: string; permissions: string[] }>,
  ) => api.patch<Role>(`/roles/${id}`, body),

  remove: (id: number) => api.delete<void>(`/roles/${id}`),
};

/** 单位（CR-073）。列表所有人能调（按范围），开、改名、归档只有超管 */
export const orgsApi = {
  list: () => api.get<Org[]>('/orgs'),

  create: (name: string) => api.post<Org>('/orgs', { name }),

  update: (id: number, body: Partial<{ name: string; status: OrgStatus }>) =>
    api.patch<Org>(`/orgs/${id}`, body),
};

/** 审计日志（CR-040）。要 audit.view */
export const auditApi = {
  list: (params: {
    from?: string;
    to?: string;
    action?: string;
    actorId?: number;
    /** 平台账号按单位筛（CR-073）：单位 id，或 `platform` = 只看平台上的事 */
    org?: string;
    page?: number;
    pageSize?: number;
  }) => api.get<Paginated<AuditLogEntry>>('/audit-logs', params),

  actions: () => api.get<string[]>('/audit-logs/actions'),

  /** 日志里出现过的操作人（CR-082 的「操作人」筛选） */
  actors: () => api.get<Array<{ id: number; name: string; employeeNo: string }>>('/audit-logs/actors'),
};

/** 我记过的一笔采购（`me/purchases` 的形状，不带提交人） */
export interface MyPurchase {
  id: string;
  siteId: number;
  purchasedAt: string;
  vendor: string | null;
  category: string | null;
  amountCents: number;
  currency: string;
  note: string | null;
  status: PurchaseStatus;
  reviewNote: string | null;
  /** 提交时刻。服务端自动记的，改不了（CR-033） */
  createdAt: string;
  receipts: Array<{ id: string; url: string; thumbUrl: string | null }>;
}

export const purchasesApi = {
  /** 我在这个工地记过的（CR-042：一律带工地，采购不再跨工地） */
  /** CR-048：和「本工地」同一套区间，服务端筛；状态桶在前端分（计数要看全部状态） */
  mine: (siteId: number, params: { from?: string; to?: string; status?: PurchaseStatus } = {}) =>
    api.get<MyPurchase[]>('/me/purchases', { siteId, ...params }),
};
